import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Users, Plus, Save, X, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Building2, Phone, Mail } from 'lucide-react';

export default function Employes({ user, onLogout }) {
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [showNewModal, setShowNewModal] = useState(false);
  const [newEmploye, setNewEmploye] = useState({
    nom: '',
    poste: '',
    departement: '',
    telephone: '',
    email: ''
  });

  useEffect(() => {
    loadEmployes();
  }, []);

  const loadEmployes = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/employes`);
      setEmployes(response.data);
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Erreur lors du chargement des employés');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (employe) => {
    setEditingId(employe.id);
    setEditedData(employe);
  };

  const handleSave = async (employeId) => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/employes/${employeId}`, editedData);
      toast.success('Employé modifié avec succès');
      setEditingId(null);
      loadEmployes();
    } catch (error) {
      console.error('Error updating employee:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDelete = async (employeId, nom) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${nom} ?`)) {
      try {
        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/employes/${employeId}`);
        toast.success(`${nom} supprimé avec succès`);
        loadEmployes();
      } catch (error) {
        console.error('Error deleting employee:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleCreateNew = async () => {
    if (!newEmploye.nom || !newEmploye.email) {
      toast.error('Veuillez remplir au moins le nom et l\'email');
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/employes`, newEmploye);
      toast.success(`${newEmploye.nom} ajouté avec succès`);
      setShowNewModal(false);
      setNewEmploye({
        nom: '',
        poste: '',
        departement: '',
        telephone: '',
        email: ''
      });
      loadEmployes();
    } catch (error) {
      console.error('Error creating employee:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditedData({});
  };

  const handleChange = (field, value) => {
    setEditedData({ ...editedData, [field]: value });
  };

  const getPosteBadgeColor = (poste) => {
    const colors = {
      'DG': 'bg-blue-100 text-blue-700',
      'Commercial': 'bg-blue-200 text-blue-800',
      'Designer': 'bg-orange-100 text-orange-700',
      'Technique': 'bg-yellow-100 text-yellow-700',
      'Finative': 'bg-green-100 text-green-700'
    };
    return colors[poste] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="employes">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="employes">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-yellow-500 rounded-lg flex items-center justify-center">
              <Users className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold">Employés</h1>
          </div>
          <Button 
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            onClick={() => setShowNewModal(true)}
          >
            <Plus className="h-4 w-4" />
            Nouveau
          </Button>
        </div>

        {/* Filter Bar */}
        <div className="flex items-center gap-4 p-4 bg-white rounded-lg border">
          <div className="flex items-center gap-2 text-gray-600">
            <Users className="h-5 w-5" />
            <span className="font-medium border-b-2 border-blue-600 pb-1">Employés</span>
          </div>
          <Button variant="outline" size="sm">Filter</Button>
          <div className="flex-1"></div>
          <Button variant="outline" size="sm">Filter</Button>
        </div>

        {/* Employees Table */}
        <Card className="border-2 shadow-lg">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <th className="text-left p-4 font-bold text-gray-700 border-r">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-blue-600" />
                        Nom
                      </div>
                    </th>
                    <th className="text-left p-4 font-bold text-gray-700 border-r">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-purple-600" />
                        Poste
                      </div>
                    </th>
                    <th className="text-left p-4 font-bold text-gray-700 border-r">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-green-600" />
                        Département
                      </div>
                    </th>
                    <th className="text-left p-4 font-bold text-gray-700 border-r">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-orange-600" />
                        Téléphone
                      </div>
                    </th>
                    <th className="text-left p-4 font-bold text-gray-700 border-r">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-red-600" />
                        Email
                      </div>
                    </th>
                    <th className="text-left p-4 font-bold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employes.map((employe, index) => (
                    <tr key={employe.id} className={`border-b-2 hover:bg-blue-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="p-4 border-r">
                        {editingId === employe.id ? (
                          <Input
                            value={editedData.nom}
                            onChange={(e) => handleChange('nom', e.target.value)}
                            className="h-8"
                          />
                        ) : (
                          <span className="font-bold text-gray-900">{employe.nom}</span>
                        )}
                      </td>
                      <td className="p-4 border-r">
                        {editingId === employe.id ? (
                          <Input
                            value={editedData.poste}
                            onChange={(e) => handleChange('poste', e.target.value)}
                            className="h-8"
                          />
                        ) : (
                          <Badge className={`${getPosteBadgeColor(employe.poste)} font-bold`}>
                            {employe.poste}
                          </Badge>
                        )}
                      </td>
                      <td className="p-4 border-r">
                        {editingId === employe.id ? (
                          <Input
                            value={editedData.departement}
                            onChange={(e) => handleChange('departement', e.target.value)}
                            className="h-8"
                          />
                        ) : (
                          <span className="text-gray-700 font-medium">{employe.departement}</span>
                        )}
                      </td>
                      <td className="p-4 border-r">
                        {editingId === employe.id ? (
                          <Input
                            value={editedData.telephone}
                            onChange={(e) => handleChange('telephone', e.target.value)}
                            className="h-8"
                          />
                        ) : (
                          <span className="text-gray-700 font-mono text-sm">{employe.telephone}</span>
                        )}
                      </td>
                      <td className="p-4 border-r">
                        {editingId === employe.id ? (
                          <Input
                            value={editedData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            className="h-8"
                          />
                        ) : (
                          <span className="text-gray-700 text-sm">{employe.email}</span>
                        )}
                      </td>
                      <td className="p-4">
                        {editingId === employe.id ? (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSave(employe.id)}
                              className="gap-1 bg-green-600 hover:bg-green-700 h-8"
                            >
                              <Save className="h-3 w-3" />
                              Enregistrer
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCancel}
                              className="gap-1 h-8"
                            >
                              <X className="h-3 w-3" />
                              Annuler
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-2"
                              onClick={() => handleEdit(employe)}
                            >
                              <Pencil className="h-3 w-3" />
                              Modifier
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(employe.id, employe.nom)}
                            >
                              <Trash2 className="h-3 w-3" />
                              Supprimer
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* New Employee Modal */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nouvel Employé</DialogTitle>
            <DialogDescription>Ajouter un nouvel employé à l'équipe SESAM</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom complet *</Label>
                <Input 
                  value={newEmploye.nom}
                  onChange={(e) => setNewEmploye({...newEmploye, nom: e.target.value})}
                  placeholder="Ex: Jean Dupont"
                />
              </div>
              <div>
                <Label>Poste</Label>
                <Input 
                  value={newEmploye.poste}
                  onChange={(e) => setNewEmploye({...newEmploye, poste: e.target.value})}
                  placeholder="Ex: Commercial"
                />
              </div>
              <div>
                <Label>Département</Label>
                <Input 
                  value={newEmploye.departement}
                  onChange={(e) => setNewEmploye({...newEmploye, departement: e.target.value})}
                  placeholder="Ex: Commercial"
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input 
                  value={newEmploye.telephone}
                  onChange={(e) => setNewEmploye({...newEmploye, telephone: e.target.value})}
                  placeholder="Ex: +236-70 XX XX XX"
                />
              </div>
              <div className="col-span-2">
                <Label>Email *</Label>
                <Input 
                  type="email"
                  value={newEmploye.email}
                  onChange={(e) => setNewEmploye({...newEmploye, email: e.target.value})}
                  placeholder="Ex: jean.dupont@sesam.cf"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowNewModal(false)}>Annuler</Button>
              <Button onClick={handleCreateNew} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                Créer l'employé
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
